import { useEffect, useCallback } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import * as ReactRouterDOM from 'react-router-dom';

const { useNavigate, useLocation } = ReactRouterDOM as any;

/**
 * Hook pour gérer le bouton retour physique Android avec React Router
 * 
 * Comportement :
 * - Si l'utilisateur n'est pas sur une page racine → naviguer en arrière
 * - Si l'utilisateur est sur une page racine (accueil) → quitter l'application
 * 
 * Pages racines considérées :
 * - / (landing page)
 * - /login
 * - /register
 * - /mobile/dashboard
 * - /dashboard
 * - /admin
 */
export const useAndroidBackButton = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Pages racines où le bouton retour doit quitter l'app
    const rootPaths = [
        '/',
        '/login',
        '/register',
        '/mobile/dashboard',
        '/dashboard',
        '/admin'
    ];

    const handleBackButton = useCallback(() => {
        const currentPath = location.pathname;
        
        // Vérifier si on est sur une page racine
        const isRootPage = rootPaths.some(rootPath => {
            // Correspondance exacte pour la plupart des routes
            if (rootPath === currentPath) return true;
            // Pour /dashboard et /admin, vérifier aussi sans trailing slash
            if (rootPath === '/dashboard' && currentPath === '/dashboard') return true;
            if (rootPath === '/admin' && currentPath === '/admin') return true;
            return false;
        });

        if (isRootPage) {
            // Sur une page racine → quitter l'application
            App.exitApp();
        } else {
            // Pas sur une page racine → naviguer en arrière
            // Utiliser navigate(-1) pour revenir dans l'historique React Router
            navigate(-1);
        }
    }, [location.pathname, navigate]);

    useEffect(() => {
        // Ne s'exécuter que sur Android natif (pas sur le web)
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
            return;
        }

        // Écouter l'événement backButton de Capacitor
        const backButtonListener = App.addListener('backButton', ({ canGoBack }) => {
            // Note: canGoBack est le canGoBack natif WebView, pas React Router
            // On ignore cette valeur et on utilise notre propre logique
            handleBackButton();
        });

        // Cleanup : retirer le listener quand le composant est démonté
        return () => {
            backButtonListener.then(listener => listener.remove());
        };
    }, [handleBackButton]);
};

export default useAndroidBackButton;
